import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight, FileText } from "lucide-react";
import api from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { resolveImg, formatDate } from "@/lib/util";
import { PublicLayout } from "@/components/site/PublicLayout";

export default function Blog() {
  const [data, setData] = useState({ items: [], pages: 1, total: 0 });
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("All");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useSeo({
    title: "Law & Society | Legal Insights by Adv. Guruvayoorappan K V",
    description: "Legal insights, awareness and perspectives on law and social matters from Adv. Guruvayoorappan K V, Advocate, Thrissur.",
  });

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 9 });
    if (active !== "All") params.set("category", active);
    if (search) params.set("search", search);
    api.get(`/blog?${params}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [active, search, page]);

  useEffect(() => { const t = setTimeout(load, search ? 400 : 0); return () => clearTimeout(t); }, [load, search]);

  return (
    <PublicLayout>
      <section className="bg-navy py-14 lg:py-20" data-testid="blog-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-brass font-mono text-xs tracking-[0.25em] uppercase mb-4">The Journal</p>
          <h1 className="font-serif text-4xl lg:text-6xl text-ivory leading-tight mb-3">Law & Society</h1>
          <p className="text-ivory/60 text-lg max-w-2xl">Legal insights, awareness and perspectives on law and social matters.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        {/* Filters */}
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center justify-between mb-10">
          <div className="flex flex-wrap gap-2" data-testid="blog-categories">
            {["All", ...categories.map((c) => c.name)].map((cat) => (
              <button key={cat} onClick={() => { setActive(cat); setPage(1); }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${active === cat ? "bg-navy text-ivory" : "bg-white border border-slate-200 text-slate-600 hover:border-brass"}`}
                data-testid={`blog-cat-${cat.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}>
                {cat}
              </button>
            ))}
          </div>
          <div className="relative lg:w-72">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search articles..."
              className="w-full pl-9 pr-4 py-2.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:border-brass" data-testid="blog-search" />
          </div>
        </div>

        {loading ? (
          <div className="py-24 text-center text-slate-400">Loading articles…</div>
        ) : data.items.length === 0 ? (
          <div className="py-24 text-center" data-testid="blog-empty">
            <FileText size={40} className="text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 text-lg">No articles published yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="blog-grid">
            {data.items.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} className="group bg-white border border-slate-200 rounded-lg overflow-hidden hover:shadow-lg hover:border-brass transition-all flex flex-col" data-testid={`blog-post-${p.slug}`}>
                <div className="overflow-hidden" style={{ aspectRatio: "3/2" }}>
                  {p.featured_image ? <img src={resolveImg(p.featured_image)} alt={p.featured_image_alt || p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full bg-navy/5" />}
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-brass text-xs font-mono uppercase tracking-widest">{p.category}</span>
                    {p.is_demo && <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full uppercase tracking-wide">Demo</span>}
                  </div>
                  <h2 className="font-serif text-lg text-navy mb-2 leading-snug group-hover:text-brass transition-colors">{p.title}</h2>
                  <p className="text-slate-500 text-sm leading-relaxed line-clamp-3 flex-1">{p.excerpt}</p>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                    <span className="text-slate-400 text-xs">{formatDate(p.published_at)}</span>
                    <span className="inline-flex items-center gap-1 text-brass text-sm font-medium">Read More <ArrowRight size={14} /></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {data.pages > 1 && (
          <div className="flex justify-center gap-2 mt-12" data-testid="blog-pagination">
            {Array.from({ length: data.pages }, (_, i) => i + 1).map((n) => (
              <button key={n} onClick={() => setPage(n)} className={`w-10 h-10 rounded-sm text-sm font-medium ${page === n ? "bg-navy text-ivory" : "bg-white border border-slate-200 text-slate-600 hover:border-brass"}`}>{n}</button>
            ))}
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
